import { createContext, useEffect, useState, ReactNode, useRef } from 'react';
import { signOut } from '@/lib/auth';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { User } from '@/graphql/types/user';
import { useNavigate } from 'react-router-dom';
import { AppRouters } from '@/utils/constants/routers';

export const USER_KEY = 'user';
export const REMEMBER_ME_KEY = 'rememberMe';
export const TOKEN_KEY = 'token';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  token: string | null | undefined;
  getUser: (user: User) => Promise<void>;
  getToken: (token: string | null | undefined) => Promise<void>;
  logout: () => void;
  logoutAdmin: () => void;
  setAuthLoading: (loading: boolean) => void;
  clearAuth: () => void;
}

export const AuthContext = createContext<AuthContextType>({} as AuthContextType);

// Helper functions for resilient initialization from localStorage
const getInitialUser = (): User | null => {
  try {
    const savedUser = localStorage.getItem(USER_KEY);
    if (!savedUser) {
      return null;
    }

    const user = JSON.parse(savedUser);

    // Validate user object structure
    if (!user || typeof user !== 'object' || !user.uid) {
      localStorage.removeItem(USER_KEY);
      return null;
    }

    return user;
  } catch (error) {
    localStorage.removeItem(USER_KEY);
    return null;
  }
};

const getInitialToken = (): string | null => {
  try {
    const savedToken = localStorage.getItem(TOKEN_KEY);
    if (!savedToken) {
      return null;
    }

    const token = JSON.parse(savedToken);

    // Validate token structure
    if (!token || typeof token !== 'string' || token.length < 10) {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }

    return token;
  } catch (error) {
    localStorage.removeItem(TOKEN_KEY);
    return null;
  }
};

const getInitialLoading = (): boolean => {
  // Smart loading logic: only load when token exists (critical for refresh)
  const hasToken = !!localStorage.getItem(TOKEN_KEY);

  // If we have a token but missing user, still treat as loading for potential recovery
  return hasToken;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const loadingRef = useRef<boolean>(getInitialLoading());

  // Initialize state from localStorage immediately to avoid race conditions
  const [user, setUser] = useState<User | null>(getInitialUser());
  const [loading, setLoading] = useState(getInitialLoading());
  const [token, setToken] = useState<string | null>(getInitialToken());

  const getUser = async (user: User) => {
    setUser(user);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  };

  const getToken = async (token: string | null | undefined) => {
    setToken(token || null);
    localStorage.setItem(TOKEN_KEY, JSON.stringify(token));
  };

  const reset = () => {
    setUser(null);
    setToken(null);
    setLoading(false);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REMEMBER_ME_KEY);
  };

  const setAuthLoading = (loading: boolean) => {
    loadingRef.current = loading;
    setLoading(loading);
  };

  const clearAuth = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
  };

  const logoutAdmin = () => {
    signOut();
    reset();
    navigate(AppRouters.admin);
  };

  const logout = () => {
    signOut();
    reset();
    navigate(AppRouters.login);
  };

  useEffect(() => {
    // Firebase auth state listener - CRITICAL for sync
    const unsubscribe = onAuthStateChanged(auth, () => {
      // Monitor Firebase auth state changes for sync issues
    });

    const savedUser = localStorage.getItem(USER_KEY);
    const savedToken = localStorage.getItem(TOKEN_KEY);
    const rememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true';

    // Resilient auth data checking with recovery mechanisms
    if (!savedToken) {
      // No token means no authentication possible
      setAuthLoading(false);
    } else if (!savedUser) {
      // Token exists, but user data missing — give it some time to recover (e.g. via refresh)
      const recoveryTimeout = setTimeout(() => {
        const userMissing = !localStorage.getItem(USER_KEY);
        const tokenStillPresent = !!localStorage.getItem(TOKEN_KEY);

        if (userMissing && tokenStillPresent && loadingRef.current) {
          setAuthLoading(false);
        }
      }, 5000); // 5-second timeout for recovery

      // Cleanup timeout if component unmounts
      return () => {
        clearTimeout(recoveryTimeout);
        unsubscribe();
      };
    } else {
      // Both user and token exist — no loading needed
      setAuthLoading(false);
    }

    // Session timeout logic (only if not remember me)
    if (!rememberMe && savedUser && savedToken) {
      const timeoutEnv = import.meta.env.VITE_SESSION_TIMEOUT;
      const timeout = timeoutEnv ? Number(timeoutEnv) : null;

      // Only set timeout if we have a valid positive number
      if (timeout && !isNaN(timeout) && timeout > 0) {
        const sessionTimer = setTimeout(() => logout(), timeout);

        return () => {
          clearTimeout(sessionTimer);
          unsubscribe();
        };
      }
    }

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        token,
        getUser,
        getToken,
        logout,
        logoutAdmin,
        setAuthLoading,
        clearAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
